import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Accordion, Card, Alert, SplitButton, Dropdown } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../services/AuthService';
import { authorizedRequest } from "../../api/authorizedRequest";
import dayjs from 'dayjs';
import { useLuckyOrange } from '../../hooks/useLuckyOrange';

const currentUser = AuthService.getCurrentUser();
  const userRoles = currentUser ? (Array.isArray(currentUser.roles) ? currentUser.roles : [currentUser.roles]) : [];

interface Application {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    birthDate: string;
    streetAddress: string;
    addressLine2?: string;
    city: string;
    stateOrProvince: string;
    postalCode: string;
    jobAppliedFor: string;
    referenceName1: string;
    referenceTitle1: string;
    referenceRelationship1: string;
    referenceEmail1: string;
    referencePhoneNumber1: string;
    referenceName2: string;
    referenceTitle2: string;
    referenceRelationship2: string;
    referenceEmail2: string;
    referencePhoneNumber2: string;
    previousWorkPlace1?: string;
    previousJobTitle1?: string;
    previousJobStartDate1?: string;
    previousJobEndDate1?: string;
    previousJobPhoneNumber1?: string;
    previousWorkPlace2?: string;
    previousJobTitle2?: string;
    previousJobStartDate2?: string;
    previousJobEndDate2?: string;
    previousJobPhoneNumber2?: string;
    creationTime: string;
    lastModificationTime?: string;
    status: number;
    resumePath?: string;
    affiliatedUnion?: string;
    dateHired?: string;
    position?: string;
    wageScale?: string;
    firstWeekOfWork?: string;
    notes?: string;
    sageEmployeeId?: string;
}

interface ApplicationDetailsModalProps {
    show: boolean;
    onClose: () => void;
    applicationId: string;
    onStatusUpdate: (applicationId: string, newStatus: string, dateHired: string, position: string, wageScale: string, firstWeekOfWork: string, notes: string, sageEmployeeId: string) => void;
    onSave?: (applicationId: string, updatedData: Partial<Application>) => void;
}

export const ApplicationDetailsModal: React.FC<ApplicationDetailsModalProps> = ({
    show,
    onClose,
    applicationId,
    onStatusUpdate,
    onSave,
}) => {
    // Existing states
    const [notes, setNotes] = useState<string>('');
    const [dateHired, setDateHired] = useState<string>('');
    const [position, setPosition] = useState<string>('');
    const [wageScale, setWageScale] = useState<string>('');
    const [firstWeekOfWork, setFirstWeekOfWork] = useState<string>('');
    const [sageEmployeeId, setSageEmployeeId] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSendingSafetyDocs, setIsSendingSafetyDocs] = useState<boolean>(false);
    const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
    const [emailInput, setEmailInput] = useState<string>('');
    const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
    
    // Application data state
    const [application, setApplication] = useState<Application | null>(null);
    const [fetchingApplication, setFetchingApplication] = useState<boolean>(false);
    
    // New editable field states
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [birthDate, setBirthDate] = useState<string>('');
    const [streetAddress, setStreetAddress] = useState<string>('');
    const [addressLine2, setAddressLine2] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [stateOrProvince, setStateOrProvince] = useState<string>('');
    const [postalCode, setPostalCode] = useState<string>('');
    const [jobAppliedFor, setJobAppliedFor] = useState<string>('');
    const [referenceName1, setReferenceName1] = useState<string>('');
    const [referenceTitle1, setReferenceTitle1] = useState<string>('');
    const [referenceRelationship1, setReferenceRelationship1] = useState<string>('');
    const [referenceEmail1, setReferenceEmail1] = useState<string>('');
    const [referencePhoneNumber1, setReferencePhoneNumber1] = useState<string>('');
    const [referenceName2, setReferenceName2] = useState<string>('');
    const [referenceTitle2, setReferenceTitle2] = useState<string>('');
    const [referenceRelationship2, setReferenceRelationship2] = useState<string>('');
    const [referenceEmail2, setReferenceEmail2] = useState<string>('');
    const [referencePhoneNumber2, setReferencePhoneNumber2] = useState<string>('');
    const [previousWorkPlace1, setPreviousWorkPlace1] = useState<string>('');
    const [previousJobTitle1, setPreviousJobTitle1] = useState<string>('');
    const [previousJobStartDate1, setPreviousJobStartDate1] = useState<string>('');
    const [previousJobEndDate1, setPreviousJobEndDate1] = useState<string>('');
    const [previousJobPhoneNumber1, setPreviousJobPhoneNumber1] = useState<string>('');
    const [previousWorkPlace2, setPreviousWorkPlace2] = useState<string>('');
    const [previousJobTitle2, setPreviousJobTitle2] = useState<string>('');
    const [previousJobStartDate2, setPreviousJobStartDate2] = useState<string>('');
    const [previousJobEndDate2, setPreviousJobEndDate2] = useState<string>('');
    const [previousJobPhoneNumber2, setPreviousJobPhoneNumber2] = useState<string>('');
    const [affiliatedUnion, setAffiliatedUnion] = useState<string>('');
    
    // Validation and alert states
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [alertType, setAlertType] = useState<'success' | 'danger'>('success');
    
    const { token } = useAuth();
    const navigate = useNavigate();

    // ── Lucky Orange analytics ──────────────────────────────────────
    const { trackMilestone, identifyApplication } = useLuckyOrange();

    // Resolve the single role that triggered this approval/rejection action.
    // HR-Manager acts on 'Applied'; CFO acts on 'HRManagerApproved'.
    const actingRole =
      userRoles.find((r) => ['HR-Manager', 'CFO'].includes(r)) ??
      userRoles[0] ??
      'Unknown';

    // Fetch application details from backend
    const fetchApplicationDetails = async (appId: string) => {
        setFetchingApplication(true);
        try {
            const response = await authorizedRequest({
                url: `/JobApplication/${appId}`,
                method: 'GET',
            });
            setApplication(response.data);
        } catch (error) {
            console.error('Error fetching application details:', error);
            setAlertMessage('Failed to load application details.');
            setAlertType('danger');
            setShowAlert(true);
        } finally {
            setFetchingApplication(false);
        }
    };


    // Effect to fetch application data when modal opens or applicationId changes
    useEffect(() => {
        if (show && applicationId) {
            // Clear validation errors and alerts when modal opens
            setValidationErrors({});
            setShowAlert(false);
            setApplication(null); // Reset application data
            
            // Fetch application details
            fetchApplicationDetails(applicationId);
        }
    }, [show, applicationId]);

    // Effect to populate form fields when application data is loaded
    useEffect(() => {
        if (application) {
            
            setNotes(application.notes || '');
            setIsLoading(false); // Reset loading state when modal opens
            
            // Format dateHired to YYYY-MM-DD format for date input
            let formattedDateHired = '';
            if (application.dateHired) {
                try {
                    const date = new Date(application.dateHired);
                    if (!isNaN(date.getTime())) {
                        formattedDateHired = date.toISOString().split('T')[0];
                    }
                } catch (error) {
                    console.warn('Invalid date format for dateHired:', application.dateHired);
                }
            }
            setDateHired(formattedDateHired);
            setPosition(application.position || '');
            setWageScale(application.wageScale || '');
            setFirstWeekOfWork(application.firstWeekOfWork || '');
            setSageEmployeeId(application.sageEmployeeId || '');
            
            // Set all editable fields
            setFirstName(application.firstName || '');
            setLastName(application.lastName || '');
            setEmail(application.email || '');
            setPhoneNumber(application.phoneNumber || '');
            setBirthDate(application.birthDate ? new Date(application.birthDate).toISOString().split('T')[0] : '');
            setStreetAddress(application.streetAddress || '');
            setAddressLine2(application.addressLine2 || '');
            setCity(application.city || '');
            setStateOrProvince(application.stateOrProvince || '');
            setPostalCode(application.postalCode || '');
            setJobAppliedFor(application.jobAppliedFor || '');
            setReferenceName1(application.referenceName1 || '');
            setReferenceTitle1(application.referenceTitle1 || '');
            setReferenceRelationship1(application.referenceRelationship1 || '');
            setReferenceEmail1(application.referenceEmail1 || '');
            setReferencePhoneNumber1(application.referencePhoneNumber1 || '');
            setReferenceName2(application.referenceName2 || '');
            setReferenceTitle2(application.referenceTitle2 || '');
            setReferenceRelationship2(application.referenceRelationship2 || '');
            setReferenceEmail2(application.referenceEmail2 || '');
            setReferencePhoneNumber2(application.referencePhoneNumber2 || '');
            setPreviousWorkPlace1(application.previousWorkPlace1 || '');
            setPreviousJobTitle1(application.previousJobTitle1 || '');
            setPreviousJobStartDate1(application.previousJobStartDate1 ? new Date(application.previousJobStartDate1).toISOString().split('T')[0] : '');
            setPreviousJobEndDate1(application.previousJobEndDate1 ? new Date(application.previousJobEndDate1).toISOString().split('T')[0] : '');
            setPreviousJobPhoneNumber1(application.previousJobPhoneNumber1 || '');
            setPreviousWorkPlace2(application.previousWorkPlace2 || '');
            setPreviousJobTitle2(application.previousJobTitle2 || '');
            setPreviousJobStartDate2(application.previousJobStartDate2 ? new Date(application.previousJobStartDate2).toISOString().split('T')[0] : '');
            setPreviousJobEndDate2(application.previousJobEndDate2 ? new Date(application.previousJobEndDate2).toISOString().split('T')[0] : '');
            setPreviousJobPhoneNumber2(application.previousJobPhoneNumber2 || '');
            setAffiliatedUnion(application.affiliatedUnion || '');
        } else if (show) {
            // Reset form fields on modal open when no application data
            setNotes('');
            setDateHired('');
            setPosition('');
            setWageScale('');
            setFirstWeekOfWork('');
            setSageEmployeeId('');
            setFirstName('');
            setLastName('');
            setEmail('');
            setPhoneNumber('');
            setBirthDate('');
            setStreetAddress('');
            setAddressLine2('');
            setCity('');
            setStateOrProvince('');
            setPostalCode('');
            setJobAppliedFor('');
            setReferenceName1('');
            setReferenceTitle1('');
            setReferenceRelationship1('');
            setReferenceEmail1('');
            setReferencePhoneNumber1('');
            setReferenceName2('');
            setReferenceTitle2('');
            setReferenceRelationship2('');
            setReferenceEmail2('');
            setReferencePhoneNumber2('');
            setPreviousWorkPlace1('');
            setPreviousJobTitle1('');
            setPreviousJobStartDate1('');
            setPreviousJobEndDate1('');
            setPreviousJobPhoneNumber1('');
            setPreviousWorkPlace2('');
            setPreviousJobTitle2('');
            setPreviousJobStartDate2('');
            setPreviousJobEndDate2('');
            setPreviousJobPhoneNumber2('');
            setAffiliatedUnion('');
            setIsLoading(false); // Reset loading state
        } else {
            // Reset loading state when modal closes
            setIsLoading(false);
        }
    }, [show, application]);

    if (!application) return null;

    const statusMap: { [key: number]: string } = {
        0: 'Applied',
        1: 'HRManagerApproved',
        2: 'HRManagerRejected',
        3: 'CFOApproved',
        4: 'CFORejected',
    };

    const currentStatus = statusMap[application.status];

    const isButtonDisabled = () => {
        if (isLoading) return true; // Disable buttons during loading

        if (currentStatus === 'CFOApproved' || currentStatus === 'CFORejected') {
            return true;
        }

        const isHRManager = userRoles.includes('HR-Manager');
        const isCFO = userRoles.includes('CFO');

        // Allow the button if any applicable role's condition is met
        if (isHRManager && currentStatus === 'Applied') return false;
        if (isCFO && currentStatus === 'HRManagerApproved') return false;

        //if (isHRManager || isCFO) return true;

        return true; // Default: no role match
    };

    const validateApprovalFields = () => {
        return dateHired.trim() !== '' && 
               wageScale.trim() !== '' && 
               firstWeekOfWork.trim() !== '';
    };

    const handleApprove = async () => {
        if (!application) return;

        // Clear previous alerts
        setShowAlert(false);

        // Validate form before approving using validateForm() which includes mandatory field checks
        if (!validateForm()) {
            // Get list of error fields for more specific feedback
            const errorFields = Object.keys(validationErrors).filter(key => validationErrors[key]);
            const errorCount = errorFields.length;
            
            let errorMessage = `Please fix the following validation error${errorCount > 1 ? 's' : ''} before approving:\n`;
            errorFields.forEach(field => {
                const fieldName = field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                errorMessage += `• ${fieldName}: ${validationErrors[field]}\n`;
            });
            
            showErrorAlert(errorMessage);
            
            // Scroll to first accordion with errors
            const firstErrorField = errorFields[0];
            if (firstErrorField) {
                // Determine which accordion section contains the error
                let accordionKey = "0"; // Default to Personal Information
                
                if (['referenceName1', 'referenceEmail1', 'referencePhoneNumber1', 'referenceName2', 'referenceEmail2', 'referencePhoneNumber2'].includes(firstErrorField)) {
                    accordionKey = "1"; // References
                } else if (['previousWorkPlace1', 'previousJobTitle1', 'previousJobStartDate1', 'previousJobEndDate1', 'previousJobPhoneNumber1', 'previousWorkPlace2', 'previousJobTitle2', 'previousJobStartDate2', 'previousJobEndDate2', 'previousJobPhoneNumber2'].includes(firstErrorField)) {
                    accordionKey = "2"; // Work History
                } else if (['dateHired', 'wageScale', 'firstWeekOfWork', 'notes'].includes(firstErrorField)) {
                    accordionKey = "3"; // HR Actions
                }
                
                // Try to expand the relevant accordion
                const accordionButton = document.querySelector(`[data-bs-target="#accordion-collapse-${accordionKey}"]`) as HTMLElement;
                if (accordionButton && accordionButton.classList.contains('collapsed')) {
                    accordionButton.click();
                }
            }
            
            return;
        }

        // Also run the field validation for additional checks
        const fieldErrors = validateFields();
        if (Object.keys(fieldErrors).length > 0) {
            setValidationErrors(fieldErrors);
            showErrorAlert('Please fix the validation errors before approving.');
            return;
        }

        // Additional specific validation for approval fields
        if (!validateApprovalFields()) {
            showErrorAlert('Date Hired, Wage Scale, and First Week of Work are required for approval.');
            return;
        }

        setIsLoading(true);
        try {
            await onStatusUpdate(applicationId, 'Approved', dateHired, position, wageScale, firstWeekOfWork, notes, sageEmployeeId);
            // MILESTONE: Application Approved
            // applicationId is auto-included by the provider; role name is the
            // only extra context needed here (no PII — role is not personal data).
            identifyApplication(applicationId);
            trackMilestone('Application Approved', { role: actingRole });
        } finally {
            setIsLoading(false);
        }
    };

    const handleReject = async () => {
        // For rejection, hiring information fields are optional, only notes might be relevant
        setIsLoading(true);
        try {
            await onStatusUpdate(applicationId, 'Rejected', dateHired, position, wageScale, firstWeekOfWork, notes, sageEmployeeId);
            // MILESTONE: Application Rejected
            identifyApplication(applicationId);
            trackMilestone('Application Rejected', { role: actingRole });
        } finally {
            setIsLoading(false);
        }
    };

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhoneNumber = (phone: string): boolean => {
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
        return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
    };

    const validatePostalCode = (postalCode: string): boolean => {
        // Basic validation for US ZIP codes and Canadian postal codes
        const postalRegex = /^(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d)$/;
        return postalRegex.test(postalCode);
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        // Required field validations
        if (!firstName.trim()) errors.firstName = 'First name is required';
        if (!lastName.trim()) errors.lastName = 'Last name is required';
        if (!email.trim()) {
            errors.email = 'Email is required';
        } else if (!validateEmail(email)) {
            errors.email = 'Please enter a valid email address';
        }
        if (!phoneNumber.trim()) {
            errors.phoneNumber = 'Phone number is required';
        } else if (!validatePhoneNumber(phoneNumber)) {
            errors.phoneNumber = 'Please enter a valid phone number';
        }
        if (!birthDate) errors.birthDate = 'Birth date is required';
        if (!streetAddress.trim()) errors.streetAddress = 'Street address is required';
        if (!city.trim()) errors.city = 'City is required';
        if (!stateOrProvince.trim()) errors.stateOrProvince = 'State/Province is required';
        if (!postalCode.trim()) {
            errors.postalCode = 'Postal code is required';
        } 
        if (!jobAppliedFor.trim()) errors.jobAppliedFor = 'Job applied for is required';

        // Hiring information validation - now mandatory fields
        if (!dateHired) errors.dateHired = 'Date hired is required';
        if (!wageScale.trim()) errors.wageScale = 'Wage scale is required';
        if (!firstWeekOfWork) errors.firstWeekOfWork = 'First week of work is required';

        // Date validations - removed age check for birth date
        if (previousJobStartDate1 && previousJobEndDate1) {
            if (new Date(previousJobStartDate1) >= new Date(previousJobEndDate1)) {
                errors.previousJobEndDate1 = 'End date must be after start date';
            }
        }

        if (previousJobStartDate2 && previousJobEndDate2) {
            if (new Date(previousJobStartDate2) >= new Date(previousJobEndDate2)) {
                errors.previousJobEndDate2 = 'End date must be after start date';
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const showSuccessAlert = (message: string) => {
        setAlertType('success');
        setAlertMessage(message);
        setShowAlert(true);
        // Remove setTimeout to keep message visible until manually dismissed
    };

    const showErrorAlert = (message: string) => {
        setAlertType('danger');
        setAlertMessage(message);
        setShowAlert(true);
        // Remove setTimeout to keep message visible until manually dismissed
    };

    const clearValidationError = (fieldName: string) => {
        if (validationErrors[fieldName]) {
            setValidationErrors(prev => ({ ...prev, [fieldName]: '' }));
        }
    };

    const validateFields = () => {
        const errors: { [key: string]: string } = {};
        // Required and max length
        if (!firstName.trim()) errors.firstName = 'First Name is required.';
        else if (firstName.length > 50) errors.firstName = 'First Name cannot exceed 50 characters.';
        if (!lastName.trim()) errors.lastName = 'Last Name is required.';
        else if (lastName.length > 50) errors.lastName = 'Last Name cannot exceed 50 characters.';
        if (!email.trim()) errors.email = 'Email is required.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Invalid email format.';
        else if (email.length > 100) errors.email = 'Email cannot exceed 100 characters.';
        if (!phoneNumber.trim()) errors.phoneNumber = 'Phone Number is required.';
        else if (!/^\+?[0-9\-()\s]+$/.test(phoneNumber)) errors.phoneNumber = 'Invalid phone number format.';
        else if (phoneNumber.length > 20) errors.phoneNumber = 'Phone Number cannot exceed 20 characters.';
        if (!birthDate) errors.birthDate = 'Birth Date is required.';
        if (!streetAddress.trim()) errors.streetAddress = 'Street Address is required.';
        else if (streetAddress.length > 150) errors.streetAddress = 'Street Address cannot exceed 150 characters.';
        if (addressLine2 && addressLine2.length > 150) errors.addressLine2 = 'Address Line 2 cannot exceed 150 characters.';
        if (!city.trim()) errors.city = 'City is required.';
        else if (city.length > 100) errors.city = 'City cannot exceed 100 characters.';
        if (!stateOrProvince.trim()) errors.stateOrProvince = 'State/Province is required.';
        else if (stateOrProvince.length > 100) errors.stateOrProvince = 'State/Province cannot exceed 100 characters.';
        if (!postalCode.trim()) errors.postalCode = 'Postal Code is required.';
        else if (postalCode.length > 20) errors.postalCode = 'Postal Code cannot exceed 20 characters.';
        if (!jobAppliedFor.trim()) errors.jobAppliedFor = 'Job Applied For is required.';
        else if (jobAppliedFor.length > 100) errors.jobAppliedFor = 'Job Applied For cannot exceed 100 characters.';
        // Reference fields
        if (referenceName1 && referenceName1.length > 100) errors.referenceName1 = 'Reference Name 1 cannot exceed 100 characters.';
        if (referenceTitle1 && referenceTitle1.length > 100) errors.referenceTitle1 = 'Reference Title 1 cannot exceed 100 characters.';
        if (referenceRelationship1 && referenceRelationship1.length > 100) errors.referenceRelationship1 = 'Reference Relationship 1 cannot exceed 100 characters.';
        if (referenceEmail1 && (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(referenceEmail1) || referenceEmail1.length > 100)) errors.referenceEmail1 = 'Invalid or too long Reference Email 1.';
        if (referencePhoneNumber1 && referencePhoneNumber1.length > 20) errors.referencePhoneNumber1 = 'Reference Phone Number 1 cannot exceed 20 characters.';
        if (referenceName2 && referenceName2.length > 100) errors.referenceName2 = 'Reference Name 2 cannot exceed 100 characters.';
        if (referenceTitle2 && referenceTitle2.length > 100) errors.referenceTitle2 = 'Reference Title 2 cannot exceed 100 characters.';
        if (referenceRelationship2 && referenceRelationship2.length > 100) errors.referenceRelationship2 = 'Reference Relationship 2 cannot exceed 100 characters.';
        if (referenceEmail2 && (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(referenceEmail2) || referenceEmail2.length > 100)) errors.referenceEmail2 = 'Invalid or too long Reference Email 2.';
        if (referencePhoneNumber2 && referencePhoneNumber2.length > 20) errors.referencePhoneNumber2 = 'Reference Phone Number 2 cannot exceed 20 characters.';
        // Previous work
        if (previousWorkPlace1 && previousWorkPlace1.length > 150) errors.previousWorkPlace1 = 'Previous Work Place 1 cannot exceed 150 characters.';
        if (previousJobTitle1 && previousJobTitle1.length > 100) errors.previousJobTitle1 = 'Previous Job Title 1 cannot exceed 100 characters.';
        if (previousJobPhoneNumber1 && previousJobPhoneNumber1.length > 20) errors.previousJobPhoneNumber1 = 'Previous Job Phone Number 1 cannot exceed 20 characters.';
        if (previousWorkPlace2 && previousWorkPlace2.length > 150) errors.previousWorkPlace2 = 'Previous Work Place 2 cannot exceed 150 characters.';
        if (previousJobTitle2 && previousJobTitle2.length > 100) errors.previousJobTitle2 = 'Previous Job Title 2 cannot exceed 100 characters.';
        if (previousJobPhoneNumber2 && previousJobPhoneNumber2.length > 20) errors.previousJobPhoneNumber2 = 'Previous Job Phone Number 2 cannot exceed 20 characters.';
        if (affiliatedUnion && affiliatedUnion.length > 100) errors.affiliatedUnion = 'Affiliated Union cannot exceed 100 characters.';
        if (position && position.length > 20) errors.position = 'Position cannot exceed 20 characters.';
        if (wageScale && wageScale.length > 50) errors.wageScale = 'Wage Scale cannot exceed 50 characters.';
        if (sageEmployeeId && sageEmployeeId.length > 10) errors.sageEmployeeId = 'Sage Employee ID cannot exceed 10 characters.';
        if (notes && notes.length > 2000) errors.notes = 'Notes cannot exceed 2000 characters.';
        return errors;
    };

    const handleSave = async () => {
        if (!application) return;

        // Clear previous alerts
        setShowAlert(false);

        // Validate form before submitting using validateForm() which includes mandatory field checks
        if (!validateForm()) {
            // Get list of error fields for more specific feedback
            const errorFields = Object.keys(validationErrors).filter(key => validationErrors[key]);
            const errorCount = errorFields.length;
            
            let errorMessage = `Please fix the following validation error${errorCount > 1 ? 's' : ''}:\n`;
            errorFields.forEach(field => {
                const fieldName = field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                errorMessage += `• ${fieldName}: ${validationErrors[field]}\n`;
            });
            
            showErrorAlert(errorMessage);
            
            // Scroll to first accordion with errors
            const firstErrorField = errorFields[0];
            if (firstErrorField) {
                // Determine which accordion section contains the error
                let accordionKey = "0"; // Default to Personal Information
                
                if (['referenceName1', 'referenceEmail1', 'referencePhoneNumber1', 'referenceName2', 'referenceEmail2', 'referencePhoneNumber2'].includes(firstErrorField)) {
                    accordionKey = "1"; // References
                } else if (['previousWorkPlace1', 'previousJobTitle1', 'previousJobStartDate1', 'previousJobEndDate1', 'previousJobPhoneNumber1', 'previousWorkPlace2', 'previousJobTitle2', 'previousJobStartDate2', 'previousJobEndDate2', 'previousJobPhoneNumber2'].includes(firstErrorField)) {
                    accordionKey = "2"; // Work History
                } else if (['dateHired', 'wageScale', 'firstWeekOfWork', 'notes'].includes(firstErrorField)) {
                    accordionKey = "3"; // HR Actions
                }
                
                // Try to expand the relevant accordion
                const accordionButton = document.querySelector(`[data-bs-target="#accordion-collapse-${accordionKey}"]`) as HTMLElement;
                if (accordionButton && accordionButton.classList.contains('collapsed')) {
                    accordionButton.click();
                }
            }
            
            return;
        }

        // Also run the field validation for additional checks
        const fieldErrors = validateFields();
        if (Object.keys(fieldErrors).length > 0) {
            setValidationErrors(fieldErrors);
            showErrorAlert('Please fix the validation errors before saving.');
            return;
        }

        setIsLoading(true);
        try {
            // Prepare the payload for UpdateJobApplicationDto
            const payload = {
                Id: applicationId,
                FirstName: firstName,
                LastName: lastName,
                Email: email,
                PhoneNumber: phoneNumber,
                BirthDate: birthDate ? dayjs(birthDate).toISOString() : null,
                StreetAddress: streetAddress,
                AddressLine2: addressLine2,
                City: city,
                StateOrProvince: stateOrProvince,
                PostalCode: postalCode,
                JobAppliedFor: jobAppliedFor,
                ReferenceName1: referenceName1,
                ReferenceTitle1: referenceTitle1,
                ReferenceRelationship1: referenceRelationship1,
                ReferenceEmail1: referenceEmail1,
                ReferencePhoneNumber1: referencePhoneNumber1,
                ReferenceName2: referenceName2,
                ReferenceTitle2: referenceTitle2,
                ReferenceRelationship2: referenceRelationship2,
                ReferenceEmail2: referenceEmail2,
                ReferencePhoneNumber2: referencePhoneNumber2,
                PreviousWorkPlace1: previousWorkPlace1,
                PreviousJobTitle1: previousJobTitle1,
                PreviousJobStartDate1: previousJobStartDate1 ? dayjs(previousJobStartDate1).toISOString() : null,
                PreviousJobEndDate1: previousJobEndDate1 ? dayjs(previousJobEndDate1).toISOString() : null,
                PreviousJobPhoneNumber1: previousJobPhoneNumber1,
                PreviousWorkPlace2: previousWorkPlace2,
                PreviousJobTitle2: previousJobTitle2,
                PreviousJobStartDate2: previousJobStartDate2 ? dayjs(previousJobStartDate2).toISOString() : null,
                PreviousJobEndDate2: previousJobEndDate2 ? dayjs(previousJobEndDate2).toISOString() : null,
                PreviousJobPhoneNumber2: previousJobPhoneNumber2,
                AffiliatedUnion: affiliatedUnion,
                DateHired: dateHired ? dayjs(dateHired).toISOString() : null,
                Position: position,
                WageScale: wageScale,
                FirstWeekOfWork: firstWeekOfWork,
                Notes: notes,
                SageEmployeeId: sageEmployeeId
            };

            await authorizedRequest({
                url: '/JobApplication',
                method: 'PUT',
                data: payload,
            });

            // Show success message
            showSuccessAlert('Application details saved successfully!');

            // Call onSave if provided to refresh parent component data
            if (onSave) {
                const updatedData: Partial<Application> = {
                    firstName,
                    lastName,
                    email,
                    phoneNumber,
                    birthDate,
                    streetAddress,
                    addressLine2,
                    city,
                    stateOrProvince,
                    postalCode,
                    jobAppliedFor,
                    referenceName1,
                    referenceTitle1,
                    referenceRelationship1,
                    referenceEmail1,
                    referencePhoneNumber1,
                    referenceName2,
                    referenceTitle2,
                    referenceRelationship2,
                    referenceEmail2,
                    referencePhoneNumber2,
                    previousWorkPlace1,
                    previousJobTitle1,
                    previousJobStartDate1,
                    previousJobEndDate1,
                    previousJobPhoneNumber1,
                    previousWorkPlace2,
                    previousJobTitle2,
                    previousJobStartDate2,
                    previousJobEndDate2,
                    previousJobPhoneNumber2,
                    affiliatedUnion
                };
                onSave(applicationId, updatedData);
            }

        } catch (error: any) {
            console.error('Save failed', error);
            
            // Handle server validation errors
            if (error.response && error.response.data) {
                const serverErrors = error.response.data;
                
                if (serverErrors.errors) {
                    // Handle validation errors from server
                    const errorMessages = Object.values(serverErrors.errors).flat();
                    showErrorAlert(`Validation Error: ${errorMessages.join(', ')}`);
                } else if (serverErrors.message) {
                    showErrorAlert(`Server Error: ${serverErrors.message}`);
                } else if (typeof serverErrors === 'string') {
                    showErrorAlert(`Error: ${serverErrors}`);
                } else {
                    showErrorAlert('Failed to save application details. Please try again.');
                }
            } else if (error.message) {
                showErrorAlert(`Error: ${error.message}`);
            } else {
                showErrorAlert('Failed to save application details. Please check your connection and try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

const handleDownload = async () => { 
    if (application) {
        try {
            const response = await authorizedRequest({
                url: `/JobApplication/${applicationId}/download-resume`,
                method: 'GET',
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));

            // Get extension from application.resumePath (e.g., ".pdf", ".docx")
            const extension = application.resumePath
                ? application.resumePath.substring(application.resumePath.lastIndexOf('.'))
                : '.pdf'; // fallback to .pdf if path missing

            const fileName = `${application.firstName}_resume${extension}`;
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            // Optional: revoke object URL after use
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading resume:', error);
        }
    }
};

const handleFillI9Form = () => {
    if (application) {
        navigate(`/staff-employee-packet?applicationId=${applicationId}`);
    }
};

const handleResendEmployeePacketEmail = async () => {
    if (!application) return;
    
    setIsLoading(true);
    try {
        await authorizedRequest({
            url: `/JobApplication/${applicationId}/resend-employee-packet-email`,
            method: 'GET',
        });
        showSuccessAlert('Employee packet email resent successfully!');
    } catch (error: any) {
        console.error('Error resending employee packet email:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to resend employee packet email.');
    } finally {
        setIsLoading(false);
    }
};

const handleDownloadSubmission = async () => {
    if (!application) return;

    try {
        const response = await authorizedRequest({
            url: `/JobApplication/${applicationId}/download-submission`,
            method: 'GET',
            responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Submission_${applicationId}.zip`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error: any) {
        console.error('Error downloading submission:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to download submission.');
    }
};

const handleSendSubmissionEmail = async () => {
    const emails = emailInput.split(',').map(e => e.trim()).filter(e => e.length > 0);
    if (emails.length === 0) return;

    setIsSendingEmail(true);
    try {
        await authorizedRequest({
            url: `/JobApplication/${applicationId}/email-submission`,
            method: 'POST',
            data: emails,
        });
        showSuccessAlert('Submission sent successfully via email!');
        setShowEmailModal(false);
        setEmailInput('');
    } catch (error: any) {
        console.error('Error sending submission email:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to send submission email.');
    } finally {
        setIsSendingEmail(false);
    }
};

const handleSendSafetyDocs = async () => {
    if (!application) return;

    setIsSendingSafetyDocs(true);
    try {
        await authorizedRequest({
            url: `/Safety/send-safety-docs/${applicationId}`,
            method: 'POST',
        });
        showSuccessAlert('Safety documents sent successfully to the candidate.');
    } catch (error: any) {
        console.error('Error sending safety documents:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to send safety documents.');
    } finally {
        setIsSendingSafetyDocs(false);
    }
};


    return (
        <>
            <style>{`
                .mobile-friendly-modal .modal-dialog {
                    margin: 0.5rem;
                    max-width: calc(100% - 1rem);
                    max-height: calc(100vh - 1rem);
                }
                @media (min-width: 576px) {
                    .mobile-friendly-modal .modal-dialog {
                        margin: 1.75rem auto;
                        max-width: 95%;
                        max-height: calc(100vh - 3.5rem);
                    }
                }
                @media (min-width: 992px) {
                    .mobile-friendly-modal .modal-dialog {
                        max-width: 90%;
                        max-height: calc(100vh - 3.5rem);
                    }
                }
                .mobile-friendly-modal .modal-content {
                    height: auto;
                    max-height: 100%;
                    display: flex;
                    flex-direction: column;
                }
                .mobile-friendly-modal .modal-body {
                    overflow-y: auto;
                    flex: 1;
                }
                .compact-form .form-label {
                    font-size: 0.875rem;
                    font-weight: 600;
                    margin-bottom: 0.25rem;
                }
                .compact-form .form-control {
                    font-size: 0.875rem;
                    padding: 0.375rem 0.5rem;
                }
                .section-header {
                    background: #f8f9fa;
                    padding: 0.5rem 0.75rem;
                    margin: 0 -0.5rem 0.75rem -0.5rem;
                    border-left: 4px solid #007bff;
                    font-weight: 600;
                    font-size: 0.95rem;
                }
                .status-badge {
                    display: inline-block;
                    padding: 0.25rem 0.5rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                    border-radius: 0.25rem;
                    background: #e9ecef;
                    color: #495057;
                }
                .readonly-field {
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    padding: 0.375rem 0.5rem;
                    border-radius: 0.25rem;
                    font-size: 0.875rem;
                }
                .accordion-body {
                    overflow: hidden;
                }
                .form-control {
                    min-width: 0;
                }
                .accordion-header .accordion-button {
                    background-color: #ffffff !important;
                    border: 1px solid #dee2e6;
                    color: #212529;
                    font-weight: 500;
                }
                .accordion-header .accordion-button:focus {
                    background-color: #f8f9fa !important;
                    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
                    border-color: #86b7fe;
                }
                .accordion-header .accordion-button:not(.collapsed) {
                    background-color: #495057 !important;
                    color: #ffffff;
                    border-color: #495057;
                }
                .accordion-header .accordion-button:not(.collapsed)::after {
                    filter: brightness(0) invert(1);
                }
                .invalid-feedback {
                    font-size: 0.75rem;
                    margin-top: 0.25rem;
                    display: block !important;
                    width: 100%;
                    color: #dc3545;
                }
                .form-control.is-invalid {
                    border-color: #dc3545;
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath d='m5.8 3.6.7.7 1.4-1.4'/%3e%3c/svg%3e");
                    background-repeat: no-repeat;
                    background-position: right calc(0.375em + 0.1875rem) center;
                    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
                }
                .validation-container {
                    position: relative;
                    width: 100%;
                }
                .validation-container .invalid-feedback {
                    position: relative;
                    z-index: 1;
                }
                .validation-container .form-control.is-invalid {
                    padding-right: calc(1.5em + 0.75rem);
                }
                /* Restore Bootstrap caret for split-button toggle (style.css replaces it with a broken FA4 icon) */
                .btn-group .dropdown-toggle::after {
                    content: "" !important;
                    display: inline-block !important;
                    margin-left: 0 !important;
                    vertical-align: 0.255em !important;
                    border-top: 0.3em solid !important;
                    border-right: 0.3em solid transparent !important;
                    border-bottom: 0 !important;
                    border-left: 0.3em solid transparent !important;
                    font-family: inherit !important;
                }
                    
            `}</style>
            <Modal show={show} onHide={onClose} size="xl" className="mobile-friendly-modal">
                <Modal.Header closeButton className="bg-light border-bottom">
                    <Modal.Title className="h5 mb-0 text-dark">Application Details --  Name : 
                        {application ? ` ${application.firstName} ${application.lastName}, Job Applied For: ${application.jobAppliedFor}, Application Status: ${currentStatus}` : 'Application Details'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-2 p-md-3">
                
                {/* Loading state while fetching application details */}
                {fetchingApplication && (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-2" />
                            <div>Loading application details...</div>
                        </div>
                    </div>
                )}
                
                {/* Alert for success/error messages */}
                {showAlert && (
                    <Alert variant={alertType} dismissible onClose={() => setShowAlert(false)} className="mb-3">
                        <div style={{ whiteSpace: 'pre-line', fontSize: '0.9rem' }}>
                            {alertMessage}
                        </div>
                    </Alert>
                )}
                
                {/* Application content - only show when not loading and application data is available */}
                {!fetchingApplication && application && (
                <div className="container-fluid compact-form">
                    <Accordion defaultActiveKey={["4"]} alwaysOpen>
                        {/* Personal Information */}
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <span className="me-2">👤</span>
                                Personal Information
                            </Accordion.Header>
                            <Accordion.Body className="p-3">
                                <div className="row g-2">
                                    <div className="col-12 col-md-6 col-lg-3">
                                        <div className="row">
                                            <div className="col-4 d-flex align-items-center">
                                                <Form.Label className="mb-0 small">First Name: <span className="text-danger">*</span></Form.Label>
                                            </div>
                                            <div className="col-8">
                                                <div className="validation-container">
                                                    <Form.Control
                                                        size="sm"
                                                        type="text"
                                                        value={firstName}
                                                        onChange={(e) => {
                                                            setFirstName(e.target.value);
                                                            clearValidationError('firstName');
                                                        }}
                                                        placeholder="First name"
                                                        isInvalid={!!validationErrors.firstName}
                                                    />
                                                    {validationErrors.firstName && (
                                                        <div className="invalid-feedback">
                                                            {validationErrors.firstName}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-3">
                                        <div className="row">
                                            <div className="col-4 d-flex align-items-center">
                                                <Form.Label className="mb-0 small">Last Name: <span className="text-danger">*</span></Form.Label>
                                            </div>
                                            <div className="col-8">
                                                <div className="validation-container">
                                                    <Form.Control
                                                        size="sm"
                                                        type="text"
                                                        value={lastName}
                                                        onChange={(e) => {
                                                            setLastName(e.target.value);
                                                            clearValidationError('lastName');
                                                        }}
                                                        placeholder="Last name"
                                                        isInvalid={!!validationErrors.lastName}
                                                    />
                                                    {validationErrors.lastName && (
                                                        <div className="invalid-feedback">
                                                            {validationErrors.lastName}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-3">
                                        <div className="row">
                                            <div className="col-4 d-flex align-items-center">
                                                <Form.Label className="mb-0 small">Email: <span className="text-danger">*</span></Form.Label>
                                            </div>
                                            <div className="col-8">
                                                <div className="validation-container">
                                                    <Form.Control
                                                        size="sm"
                                                        type="email"
                                                        value={email}
                                                        onChange={(e) => {
                                                            setEmail(e.target.value);
                                                            clearValidationError('email');
                                                        }}
                                                        placeholder="Email"
                                                        isInvalid={!!validationErrors.email}
                                                    />
                                                    {validationErrors.email && (
                                                        <div className="invalid-feedback">
                                                            {validationErrors.email}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-3">
                                        <div className="row">
                                            <div className="col-4 d-flex align-items-center">
                                                <Form.Label className="mb-0 small">Phone: <span className="text-danger">*</span></Form.Label>
                                            </div>
                                            <div className="col-8">
                                                <div className="validation-container">
                                                    <Form.Control
                                                        size="sm"
                                                        type="tel"
                                                        value={phoneNumber}
                                                        onChange={(e) => {
                                                            setPhoneNumber(e.target.value);
                                                            clearValidationError('phoneNumber');
                                                        }}
                                                        placeholder="Phone"
                                                        isInvalid={!!validationErrors.phoneNumber}
                                                    />
                                                    {validationErrors.phoneNumber && (
                                                        <div className="invalid-feedback">
                                                            {validationErrors.phoneNumber}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-3">
                                        <div className="row">
                                            <div className="col-4 d-flex align-items-center">
                                                <Form.Label className="mb-0 small">Birth Date: <span className="text-danger">*</span></Form.Label>
                                            </div>
                                            <div className="col-8">
                                                <div className="validation-container">
                                                    <Form.Control
                                                        size="sm"
                                                        type="date"
                                                        value={birthDate}
                                                        onChange={(e) => {
                                                            setBirthDate(e.target.value);
                                                            clearValidationError('birthDate');
                                                        }}
                                                        isInvalid={!!validationErrors.birthDate}
                                                    />
                                                    {validationErrors.birthDate && (
                                                        <div className="invalid-feedback">
                                                            {validationErrors.birthDate}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-3">
                                        <div className="row">
                                            <div className="col-4 d-flex align-items-center">
                                                <Form.Label className="mb-0 small">Job Applied: <span className="text-danger">*</span></Form.Label>
                                            </div>
                                            <div className="col-8">
                                                <div className="validation-container">
                                                    <Form.Control
                                                        size="sm"
                                                        type="text"
                                                        value={jobAppliedFor}
                                                        onChange={(e) => {
                                                            setJobAppliedFor(e.target.value);
                                                            clearValidationError('jobAppliedFor');
                                                        }}
                                                        placeholder="Position"
                                                        isInvalid={!!validationErrors.jobAppliedFor}
                                                    />
                                                    {validationErrors.jobAppliedFor && (
                                                        <div className="invalid-feedback">
                                                            {validationErrors.jobAppliedFor}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-3">
                                        <div className="row align-items-center">
                                            <div className="col-4">
                                                <Form.Label className="mb-0 small">Union:</Form.Label>
                                            </div>
                                            <div className="col-8">
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    value={affiliatedUnion}
                                                    onChange={(e) => setAffiliatedUnion(e.target.value)}
                                                    placeholder="Union"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-3">
                                        <div className="row">
                                            <div className="col-4 d-flex align-items-center">
                                                <Form.Label className="mb-0 small">Street: <span className="text-danger">*</span></Form.Label>
                                            </div>
                                            <div className="col-8">
                                                <div className="validation-container">
                                                    <Form.Control
                                                        size="sm"
                                                        type="text"
                                                        value={streetAddress}
                                                        onChange={(e) => {
                                                            setStreetAddress(e.target.value);
                                                            clearValidationError('streetAddress');
                                                        }}
                                                        placeholder="Street address"
                                                        isInvalid={!!validationErrors.streetAddress}
                                                    />
                                                    {validationErrors.streetAddress && (
                                                        <div className="invalid-feedback">
                                                            {validationErrors.streetAddress}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-3">
                                        <div className="row align-items-center">
                                            <div className="col-4">
                                                <Form.Label className="mb-0 small">Address 2:</Form.Label>
                                            </div>
                                            <div className="col-8">
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    value={addressLine2}
                                                    onChange={(e) => setAddressLine2(e.target.value)}
                                                    placeholder="Apt, suite"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-3">
                                        <div className="row">
                                            <div className="col-4 d-flex align-items-center">
                                                <Form.Label className="mb-0 small">City: <span className="text-danger">*</span></Form.Label>
                                            </div>
                                            <div className="col-8">
                                                <div className="validation-container">
                                                    <Form.Control
                                                        size="sm"
                                                        type="text"
                                                        value={city}
                                                        onChange={(e) => {
                                                            setCity(e.target.value);
                                                            clearValidationError('city');
                                                        }}
                                                        placeholder="City"
                                                        isInvalid={!!validationErrors.city}
                                                    />
                                                    {validationErrors.city && (
                                                        <div className="invalid-feedback">
                                                            {validationErrors.city}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-3">
                                        <div className="row">
                                            <div className="col-4 d-flex align-items-center">
                                                <Form.Label className="mb-0 small">State: <span className="text-danger">*</span></Form.Label>
                                            </div>
                                            <div className="col-8">
                                                <div className="validation-container">
                                                    <Form.Control
                                                        size="sm"
                                                        type="text"
                                                        value={stateOrProvince}
                                                        onChange={(e) => {
                                                            setStateOrProvince(e.target.value);
                                                            clearValidationError('stateOrProvince');
                                                        }}
                                                        placeholder="State"
                                                        isInvalid={!!validationErrors.stateOrProvince}
                                                    />
                                                    {validationErrors.stateOrProvince && (
                                                        <div className="invalid-feedback">
                                                            {validationErrors.stateOrProvince}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-3">
                                        <div className="row">
                                            <div className="col-4 d-flex align-items-center">
                                                <Form.Label className="mb-0 small">Postal: <span className="text-danger">*</span></Form.Label>
                                            </div>
                                            <div className="col-8">
                                                <div className="validation-container">
                                                    <Form.Control
                                                        size="sm"
                                                        type="text"
                                                        value={postalCode}
                                                        onChange={(e) => {
                                                            setPostalCode(e.target.value);
                                                            clearValidationError('postalCode');
                                                        }}
                                                        placeholder="ZIP"
                                                        isInvalid={!!validationErrors.postalCode}
                                                    />
                                                    {validationErrors.postalCode && (
                                                        <div className="invalid-feedback">
                                                            {validationErrors.postalCode}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* References */}
                        <Accordion.Item eventKey="1">
                            <Accordion.Header>
                                <span className="me-2">👥</span>
                                References
                            </Accordion.Header>
                            <Accordion.Body className="p-3">
                                <div className="row g-2">
                                    <div className="col-12 col-lg-6">
                                        <div className="p-3">
                                            <h6 className="text-primary mb-3">Reference 1</h6>
                                            <div className="row g-2">
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Name</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            value={referenceName1}
                                                            onChange={(e) => setReferenceName1(e.target.value)}
                                                            placeholder="Reference name"
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Title</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            value={referenceTitle1}
                                                            onChange={(e) => setReferenceTitle1(e.target.value)}
                                                            placeholder="Job title"
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Relationship</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            value={referenceRelationship1}
                                                            onChange={(e) => setReferenceRelationship1(e.target.value)}
                                                            placeholder="Relationship"
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Email</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="email"
                                                            value={referenceEmail1}
                                                            onChange={(e) => setReferenceEmail1(e.target.value)}
                                                            placeholder="Email"
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Phone</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="tel"
                                                            value={referencePhoneNumber1}
                                                            onChange={(e) => setReferencePhoneNumber1(e.target.value)}
                                                            placeholder="Phone"
                                                        />
                                                    </Form.Group>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-6">
                                        <div className="p-3">
                                            <h6 className="text-primary mb-3">Reference 2</h6>
                                            <div className="row g-2">
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Name</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            value={referenceName2}
                                                            onChange={(e) => setReferenceName2(e.target.value)}
                                                            placeholder="Reference name"
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Title</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            value={referenceTitle2}
                                                                onChange={(e) => setReferenceTitle2(e.target.value)}
                                                            placeholder="Job title"
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Relationship</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            value={referenceRelationship2}
                                                            onChange={(e) => setReferenceRelationship2(e.target.value)}
                                                            placeholder="Relationship"
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Email</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="email"
                                                            value={referenceEmail2}
                                                            onChange={(e) => setReferenceEmail2(e.target.value)}
                                                            placeholder="Email"
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Phone</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="tel"
                                                            value={referencePhoneNumber2}
                                                            onChange={(e) => setReferencePhoneNumber2(e.target.value)}
                                                            placeholder="Phone"
                                                        />
                                                    </Form.Group>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Work History */}
                        <Accordion.Item eventKey="2">
                            <Accordion.Header>
                                <span className="me-2">💼</span>
                                Previous Work History
                            </Accordion.Header>
                            <Accordion.Body className="p-3">
                                <div className="row g-2">
                                    <div className="col-12 col-lg-6">
                                        <div className="p-3">
                                            <h6 className="text-primary mb-3">Previous Work Place 1</h6>
                                            <div className="row g-2">
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Work Place</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            value={previousWorkPlace1}
                                                            onChange={(e) => setPreviousWorkPlace1(e.target.value)}
                                                            placeholder="Company name"
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Job Title</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            value={previousJobTitle1}
                                                            onChange={(e) => setPreviousJobTitle1(e.target.value)}
                                                            placeholder="Position"
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Start Date</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="date"
                                                            value={previousJobStartDate1}
                                                            onChange={(e) => setPreviousJobStartDate1(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>End Date</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="date"
                                                            value={previousJobEndDate1}
                                                            onChange={(e) => setPreviousJobEndDate1(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Phone Number</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="tel"
                                                            value={previousJobPhoneNumber1}
                                                            onChange={(e) => setPreviousJobPhoneNumber1(e.target.value)}
                                                            placeholder="Contact phone"
                                                        />
                                                    </Form.Group>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-6">
                                        <div className="p-3">
                                            <h6 className="text-primary mb-3">Previous Work Place 2</h6>
                                            <div className="row g-2">
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Work Place</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            value={previousWorkPlace2}
                                                            onChange={(e) => setPreviousWorkPlace2(e.target.value)}
                                                            placeholder="Company name"
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Job Title</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            value={previousJobTitle2}
                                                            onChange={(e) => setPreviousJobTitle2(e.target.value)}
                                                            placeholder="Position"
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Start Date</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="date"
                                                            value={previousJobStartDate2}
                                                            onChange={(e) => setPreviousJobStartDate2(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12 col-sm-6">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>End Date</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="date"
                                                            value={previousJobEndDate2}
                                                            onChange={(e) => setPreviousJobEndDate2(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12">
                                                    <Form.Group className="mb-2">
                                                        <Form.Label>Phone Number</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="tel"
                                                            value={previousJobPhoneNumber2}
                                                            onChange={(e) => setPreviousJobPhoneNumber2(e.target.value)}
                                                            placeholder="Contact phone"
                                                        />
                                                    </Form.Group>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Application Status & Info */}
                        <Accordion.Item eventKey="3">
                            <Accordion.Header>
                                <span className="me-2">ℹ️</span>
                                Application Status & Information
                            </Accordion.Header>
                            <Accordion.Body className="p-3">
                                <div className="row g-2 mb-3">
                                    <div className="col-12 col-sm-6 col-lg-4">
                                        <Form.Label className="small text-muted">Status</Form.Label>
                                        <div className="readonly-field">
                                            <span className="status-badge">{currentStatus}</span>
                                        </div>
                                    </div>
                                    <div className="col-12 col-sm-6 col-lg-4">
                                        <Form.Label className="small text-muted">Applied Date</Form.Label>
                                        <div className="readonly-field">
                                            {new Date(application.creationTime).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <div className="col-12 col-sm-6 col-lg-4">
                                        <Form.Label className="small text-muted">Last Modified</Form.Label>
                                        <div className="readonly-field small">
                                            {application.lastModificationTime ? new Date(application.lastModificationTime).toLocaleString() : 'N/A'}
                                        </div>
                                    </div>
                                </div>
                                <div className="row g-2">
                                    <div className="col-12">
                                        <Form.Label className="small text-muted">Resume</Form.Label>
                                        <div className="readonly-field">
                                            {application.resumePath ? (
                                                <Button size="sm" variant="outline-primary" onClick={handleDownload}>
                                                    <span className="me-1">📥</span>Download Resume
                                                </Button>
                                            ) : (
                                                <span className="text-muted">No resume uploaded</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Hiring Information */}
                        <Accordion.Item eventKey="4">
                            <Accordion.Header>
                                <span className="me-2">🤝</span>
                                Hiring Information
                            </Accordion.Header>
                            <Accordion.Body className="p-3">
                                <div className="row g-2 mb-3">
                                    <div className="col-12 col-sm-6 col-lg-4">
                                        <Form.Group className="mb-2">
                                            <Form.Label>Date Hired <span className="text-danger">*</span></Form.Label>
                                            <div className="validation-container">
                                                <Form.Control
                                                    size="sm"
                                                    type="date"
                                                    value={dateHired}
                                                    onChange={(e) => {
                                                        setDateHired(e.target.value);
                                                        clearValidationError('dateHired');
                                                    }}
                                                    isInvalid={!!validationErrors.dateHired}
                                                />
                                                {validationErrors.dateHired && (
                                                    <div className="invalid-feedback">
                                                        {validationErrors.dateHired}
                                                    </div>
                                                )}
                                            </div>
                                        </Form.Group>
                                    </div>
                                    <div className="col-12 col-sm-6 col-lg-4">
                                        <Form.Group className="mb-2">
                                            <Form.Label>Position</Form.Label>
                                            <div className="validation-container">
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    value={position}
                                                    onChange={(e) => {
                                                        setPosition(e.target.value);
                                                        clearValidationError('position');
                                                    }}
                                                    placeholder="Position"
                                                    maxLength={20}
                                                    isInvalid={!!validationErrors.position}
                                                />
                                                {validationErrors.position && (
                                                    <div className="invalid-feedback">
                                                        {validationErrors.position}
                                                    </div>
                                                )}
                                            </div>
                                        </Form.Group>
                                    </div>
                                    <div className="col-12 col-sm-6 col-lg-4">
                                        <Form.Group className="mb-2">
                                            <Form.Label>Wage Scale <span className="text-danger">*</span></Form.Label>
                                            <div className="validation-container">
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    value={wageScale}
                                                    onChange={(e) => {
                                                        setWageScale(e.target.value);
                                                        clearValidationError('wageScale');
                                                    }}
                                                    placeholder="Wage scale"
                                                    isInvalid={!!validationErrors.wageScale}
                                                />
                                                {validationErrors.wageScale && (
                                                    <div className="invalid-feedback">
                                                        {validationErrors.wageScale}
                                                    </div>
                                                )}
                                            </div>
                                        </Form.Group>
                                    </div>
                                    <div className="col-12 col-sm-6 col-lg-4">
                                        <Form.Group className="mb-2">
                                            <Form.Label>First Week of Work <span className="text-danger">*</span></Form.Label>
                                            <div className="validation-container">
                                                <Form.Control
                                                    size="sm"
                                                    type="date"
                                                    value={firstWeekOfWork}
                                                    onChange={(e) => {
                                                        setFirstWeekOfWork(e.target.value);
                                                        clearValidationError('firstWeekOfWork');
                                                    }}
                                                    isInvalid={!!validationErrors.firstWeekOfWork}
                                                />
                                                {validationErrors.firstWeekOfWork && (
                                                    <div className="invalid-feedback">
                                                        {validationErrors.firstWeekOfWork}
                                                    </div>
                                                )}
                                            </div>
                                        </Form.Group>
                                    </div>
                                    <div className="col-12 col-sm-6 col-lg-4">
                                        <Form.Group className="mb-2">
                                            <Form.Label>Sage Employee ID</Form.Label>
                                            <div className="validation-container">
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    value={sageEmployeeId}
                                                    onChange={(e) => {
                                                        setSageEmployeeId(e.target.value);
                                                        clearValidationError('sageEmployeeId');
                                                    }}
                                                    placeholder="Max 10 characters"
                                                    maxLength={10}
                                                    isInvalid={!!validationErrors.sageEmployeeId}
                                                />
                                                {validationErrors.sageEmployeeId && (
                                                    <div className="invalid-feedback">
                                                        {validationErrors.sageEmployeeId}
                                                    </div>
                                                )}
                                            </div>
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <Form.Group className="mb-2">
                                            <Form.Label>Notes</Form.Label>
                                            <Form.Control
                                                size="sm"
                                                as="textarea"
                                                rows={9}
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                placeholder="Enter notes here..."
                                                style={{ minHeight: '180px' }}
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </div>
                )}
            </Modal.Body>
            <Modal.Footer className="p-2 d-flex flex-wrap gap-1 justify-content-end">
                <Button size="sm" variant="primary" onClick={handleSave} disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-1" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <span className="me-1">💾</span>
                            Save Changes
                        </>
                    )}
                </Button>
                {currentStatus === 'CFOApproved' && (
                    <SplitButton
                        size="sm"
                        variant="info"
                        title={<><span className="me-1">📄</span>Fill Employee Packet</>}
                        onClick={handleFillI9Form}
                    >
                        <Dropdown.Item onClick={handleResendEmployeePacketEmail} disabled={isLoading}>
                            <span className="me-1">📧</span>Re-Send Employee Packet Email
                        </Dropdown.Item>
                        <Dropdown.Item onClick={handleDownloadSubmission}>
                            <span className="me-1">📥</span>Download Submission
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setShowEmailModal(true)}>
                            <span className="me-1">✉️</span>Send Submission as Email
                        </Dropdown.Item>
                    </SplitButton>
                )}
                {currentStatus === 'CFOApproved' && (
                    <Button size="sm" variant="danger" onClick={handleSendSafetyDocs} disabled={isSendingSafetyDocs}>
                        {isSendingSafetyDocs ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-1" />
                                Sending...
                            </>
                        ) : (
                            <>
                                <span className="me-1">🦺</span>
                                Send Safety Docs
                            </>
                        )}
                    </Button>
                )}

                <Button size="sm" variant="success" onClick={handleApprove} disabled={isButtonDisabled()}>
                    {isLoading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-1" />
                            Processing...
                        </>
                    ) : (
                        <>
                            <span className="me-1">✅</span>
                            Approve
                        </>
                    )}
                </Button>
                <Button size="sm" variant="danger" onClick={handleReject} disabled={isButtonDisabled()}>
                    {isLoading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-1" />
                            Processing...
                        </>
                    ) : (
                        <>
                            <span className="me-1">❌</span>
                            Reject
                        </>
                    )}
                </Button>
                <Button size="sm" variant="secondary" onClick={onClose}>
                    <span className="me-1">✖️</span>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
        <Modal show={showEmailModal} onHide={() => { setShowEmailModal(false); setEmailInput(''); }} centered>
            <Modal.Header closeButton>
                <Modal.Title className="h6">Send Submission as Email</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group>
                    <Form.Label className="small fw-semibold">Email Addresses <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="e.g. user@example.com, manager@example.com"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        disabled={isSendingEmail}
                    />
                    <Form.Text className="text-muted">Separate multiple addresses with a comma.</Form.Text>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button size="sm" variant="secondary" onClick={() => { setShowEmailModal(false); setEmailInput(''); }} disabled={isSendingEmail}>
                    Cancel
                </Button>
                <Button
                    size="sm"
                    variant="info"
                    onClick={handleSendSubmissionEmail}
                    disabled={isSendingEmail || !emailInput.trim()}
                >
                    {isSendingEmail ? (
                        <><Spinner as="span" animation="border" size="sm" className="me-1" />Sending...</>
                    ) : (
                        <><span className="me-1">✉️</span>Send</>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};
